import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * OAuth callback handler page for Advisor Tools.
 *
 * The authorization service redirects here after a successful OAuth2 login with a
 * one-time authorization code on the query string (?code=...). The code is exchanged
 * for an HttpOnly session cookie via POST /api/v1/auth/exchange; the user is then
 * rehydrated from /api/v1/auth/me. No token is read from the URL or written to browser
 * storage (SEC-V04 / GH #1780).
 *
 * Expected query format:
 *   ?code=<one-time-code>&state=<state>
 *
 * On error, the authorization service redirects with query params:
 *   ?error=<code>&message=<description>
 *
 * NOTE: the matching aaim-core oauth2/callback redirect is being reshaped from a URL
 * hash (#access_token=...) to ?code= as a single atomic step across aaim-core + all 4
 * institutional SPAs; this reader is authored ahead of that flip.
 */
export function OAuthCallbackPage() {
  const navigate = useNavigate();
  const { completeOAuth } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get('error');
    if (oauthError) {
      const message = params.get('message');
      setError(message || `Authentication failed (${oauthError}).`);
      return;
    }

    const code = params.get('code');
    if (!code) {
      setError('No authorization code received. Please try again.');
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        // Exchange the one-time code for the session cookie, then rehydrate the user.
        await completeOAuth(code);
        if (cancelled) return;

        // Strip the ?code= from the URL (keep it out of browser history).
        window.history.replaceState(null, '', '/auth/callback');

        // Clear stored OAuth state nonce
        sessionStorage.removeItem('oauth_state');

        // Redirect to dashboard
        navigate('/dashboard', { replace: true });
      } catch {
        if (!cancelled) setError('Failed to complete sign-in. Please try again.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [completeOAuth, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-surface rounded shadow-sm border border-recessed p-8 text-center">
          <div className="h-1 bg-accent -mt-8 -mx-8 mb-6 rounded-t" />
          <h1 className="text-xl font-sans font-semibold text-primary mb-4">
            Authentication Error
          </h1>
          <p className="text-sm text-tertiary mb-6">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/login', { replace: true })}
            className="px-6 py-2 bg-accent text-white text-sm font-medium rounded hover:bg-accent/90 transition-colors"
          >
            Return to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-sm text-tertiary">Completing sign-in...</p>
      </div>
    </div>
  );
}
